import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IMG_FALLBACK } from "@/components/Collections";
import { formatPrice, stockStatus } from "@/lib/api";

const onImgError = (e) => {
  if (e.currentTarget.dataset.fb) return;
  e.currentTarget.dataset.fb = "1";
  e.currentTarget.src = IMG_FALLBACK;
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function CollectionSection({
  id,
  chapter,
  title,
  tagline,
  description,
  heroImg,
  products = [],
  inverted = false,
  reverse = false,
}) {
  const navigate = useNavigate();
  const bg = inverted ? "bg-[#090909] text-[#F2EFEB]" : "bg-[#F2EFEB] text-[#090909]";
  const border = inverted ? "border-[#F2EFEB]" : "border-[#090909]";
  const items = [...products].sort((a, b) => (b.featured === true) - (a.featured === true)).slice(0, 4);

  return (
    <section
      id={id}
      data-testid={`collection-${id}`}
      className={`${bg} scroll-mt-20 px-4 md:px-8 py-16 md:py-32 border-t-2 ${border}`}
    >
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <span className="overline text-[#FF2A00]">{chapter} · {tagline}</span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-[9rem] leading-[0.8] tracking-tight mt-3 break-words">
            {title}
          </h2>
        </div>
        <p className="font-serif-ed italic text-lg md:text-2xl max-w-md self-end">
          {description}
        </p>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 ${reverse ? "lg:[direction:rtl]" : ""}`}>
        {/* Editorial hero image */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className={`lg:col-span-7 relative border-2 ${border} overflow-hidden min-h-[360px] md:min-h-[520px] group [direction:ltr] flex flex-col`}
        >
          <img
            src={(heroImg || "").trim() || IMG_FALLBACK}
            alt={title}
            onError={onImgError}
            className="w-full flex-1 min-h-[360px] md:min-h-[520px] object-cover grayscale contrast-125 group-hover:grayscale-0 transition-all duration-700"
          />
          <span className="absolute bottom-4 left-4 font-mono-ui text-xs uppercase px-3 py-1 border-2 border-[#090909] bg-[#DFFF00] text-[#090909] rotate-[-3deg]">
            {chapter} / Good Grief
          </span>
        </motion.div>

        {/* Product cards */}
        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6 [direction:ltr]">
          {items.map((p, i) => (
            <motion.button
              key={p.slug}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              onClick={() => navigate(`/product/${p.slug}`)}
              data-testid={`product-card-${p.slug}`}
              className={`text-left border-2 ${border} flex flex-col group relative`}
            >
              <div className="relative overflow-hidden">
                {p.featured && (
                  <span className="absolute top-2 left-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] bg-[#FF2A00] text-[#F2EFEB] rotate-[3deg]">
                    Pick
                  </span>
                )}
                {p.status !== "live" && (
                  <span className="absolute top-2 right-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] bg-[#DFFF00] text-[#090909] rotate-[-4deg]">
                    Soon
                  </span>
                )}
                {p.status === "live" && stockStatus(p).label && (
                  <span className={`absolute top-2 right-2 z-10 font-mono-ui text-[0.6rem] uppercase px-2 py-0.5 border-2 border-[#090909] rotate-[-4deg] ${stockStatus(p).tone === "out" ? "bg-[#FF2A00] text-[#F2EFEB]" : "bg-[#DFFF00] text-[#090909]"}`}>
                    {stockStatus(p).label}
                  </span>
                )}
                <img
                  src={p.images?.[0] || IMG_FALLBACK}
                  alt={p.name}
                  onError={onImgError}
                  className="block w-full aspect-square object-cover grayscale contrast-125 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                />
              </div>
              <div className={`border-t-2 ${border} p-3 flex items-center justify-between gap-2`}>
                <div className="min-w-0">
                  <p className="font-mono-ui text-xs uppercase leading-tight line-clamp-2">{p.name}</p>
                  <p className="font-mono-ui text-xs opacity-60 mt-0.5">${formatPrice(p.price)}</p>
                </div>
                <span className="font-display text-lg shrink-0 group-hover:translate-x-0.5 transition-transform">↗</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <div className="mt-12 flex justify-end">
        <button
          onClick={() => navigate(`/shop/${id}`)}
          data-testid={`continue-${id}`}
          className={`group inline-flex items-center gap-3 border-2 ${border} px-6 py-4 font-mono-ui uppercase text-xs tracking-widest hover:bg-[#DFFF00] hover:text-[#090909] hover:border-[#DFFF00] transition-colors`}
        >
          Continue to {title}
          <span className="font-display text-lg group-hover:translate-x-1 transition-transform">→</span>
        </button>
      </div>
    </section>
  );
}
